import clsx from "clsx";
import * as React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../collapsible/Collapsible.js";

const NavLinkContext = React.createContext(1);
type PolymorphicProps<C extends React.ElementType> = {
  component?: C;
  label: string;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  children?: React.ReactNode;
  childrenOffset?: number;
  defaultOpen?: boolean;
  withNestedPadding?: boolean;
  active?: boolean;
  className?: string;
  classNames?: {
    root?: string;
    item?: string;
    leftSection?: string;
    rightSection?: string;
    rightSectionIcon?: string;
    label?: string;
    childrenWrapper?: string;
  };
} & React.ComponentProps<C>;

const NavLink = <C extends React.ElementType = "a">({
  label,
  component: Component = "a",
  leftSection,
  rightSection,
  childrenOffset = 16,
  withNestedPadding = true,
  children,
  defaultOpen = false,
  active = false,
  className,
  classNames,
  ...props
}: PolymorphicProps<C>) => {
  const hasChildren = React.Children.count(children) > 0;
  const level = React.useContext(NavLinkContext);

  if (hasChildren) {
    return (
      <NavLinkContext.Provider value={level + 1}>
        <Collapsible defaultOpen={defaultOpen}>
          <CollapsibleTrigger asChild>
            <Component
              {...(props as React.ComponentProps<typeof Component>)}
              className={clsx(
                "ox-reset ox-NavlinkRoot",
                className,
                classNames?.root,
              )}
              data-active={active}
              style={{
                paddingLeft: withNestedPadding
                  ? `${level * childrenOffset}px`
                  : undefined,
              }}
            >
              <span
                className={clsx("ox-reset ox-NavlinkItem", classNames?.item)}
                data-active={active}
              >
                <span
                  className={clsx(
                    "ox-reset ox-NavlinkRightSection",
                    classNames?.rightSection,
                  )}
                >
                  {leftSection ? (
                    <span
                      className={clsx(
                        "ox-NavlinkLeftSectionIcon",
                        classNames?.leftSection,
                      )}
                    >
                      {leftSection}
                    </span>
                  ) : null}
                  <span
                    className={clsx(
                      "ox-NavLinkLabel ox-fz-sm",
                      classNames?.label,
                    )}
                  >
                    {label}
                  </span>
                </span>
                <span
                  className={clsx(
                    "ox-NavlinkRightSectionIcon",
                    classNames?.rightSectionIcon,
                  )}
                >
                  {rightSection ? (
                    rightSection
                  ) : (
                    <svg
                      className="ox-NavlinkRightDefaultIcon"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                    </svg>
                  )}
                </span>
              </span>
            </Component>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={clsx("ox-reset ox-ncc", classNames?.childrenWrapper)}
          >
            {children}
          </CollapsibleContent>
        </Collapsible>
      </NavLinkContext.Provider>
    );
  }

  return (
    <Component
      {...(props as React.ComponentProps<typeof Component>)}
      className={clsx("ox-reset ox-NavlinkRoot", className, classNames?.root)}
      data-active={active}
      style={{
        paddingLeft: withNestedPadding
          ? `${level * childrenOffset}px`
          : undefined,
      }}
    >
      <span
        className={clsx("ox-reset ox-NavlinkItem", classNames?.item)}
        data-active={active}
      >
        <span
          className={clsx(
            "ox-reset ox-NavlinkRightSection",
            classNames?.rightSection,
          )}
        >
          {leftSection ? (
            <span
              className={clsx(
                "ox-reset ox-NavlinkLeftSectionIcon",
                classNames?.leftSection,
              )}
            >
              {leftSection}
            </span>
          ) : null}
          <span
            className={clsx(
              "ox-reset ox-NavLinkLabel ox-fz-sm",
              classNames?.label,
            )}
          >
            {label}
          </span>
        </span>
        {rightSection ? (
          <span
            className={clsx(
              "ox-reset ox-NavlinkRightSectionIcon",
              classNames?.rightSectionIcon,
            )}
          >
            {rightSection}
          </span>
        ) : null}
      </span>
    </Component>
  );
};

NavLink.displayName = "NavLink";
export { NavLink };
export type NavLinkProps<C extends React.ElementType = "a"> =
  PolymorphicProps<C>;
